import { useTable } from '@refinedev/react-table';
import { type ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router';
import type { TfPromptRow } from '@verybigthings/trackframe-shared/schemas';
import { buttonVariants } from '@/components/ui/button';
import { DataTable } from '@/components/DataTable';
import { TableSearch } from '@/components/TableSearch';
import { fmtDate } from '@/lib/format';
import { useSearchFilter } from '@/lib/use-search-filter';
import { cn } from '@/lib/utils';

// Module scope, NOT the component body — see trackColumns for why identity
// stability is what keeps the `q` filter alive.
const promptColumns: ColumnDef<TfPromptRow>[] = [
  { id: 'name', header: 'Name', accessorKey: 'name' },
  {
    id: 'description',
    header: 'Description',
    accessorKey: 'description',
    cell: (c) => (c.getValue() as string | null) ?? '—',
  },
  {
    id: 'trackId',
    header: 'Track',
    accessorKey: 'trackId',
    cell: (c) => <span className="font-mono text-xs">{c.getValue() as string}</span>,
  },
  {
    id: 'updatedAt',
    header: 'Updated',
    accessorKey: 'updatedAt',
    cell: (c) => fmtDate(c.getValue() as string),
  },
  {
    id: 'actions',
    header: '',
    cell: (c) => (
      <div className="flex justify-end gap-2">
        <Link
          to={`/admin/prompts/show/${c.row.original.id}`}
          className={cn(buttonVariants({ size: 'sm', variant: 'ghost' }))}
        >
          View
        </Link>
        <Link
          to={`/admin/prompts/edit/${c.row.original.id}`}
          className={cn(buttonVariants({ size: 'sm', variant: 'outline' }))}
        >
          Edit
        </Link>
      </div>
    ),
  },
];

export function PromptsList() {
  const {
    reactTable: table,
    refineCore: { setFilters, filters },
  } = useTable<TfPromptRow>({ columns: promptColumns });
  const [search, setSearch] = useSearchFilter(setFilters, filters);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Prompts</h1>
          <p className="text-sm text-muted-foreground">System prompts and their versioned content.</p>
        </div>
        <Link to="/admin/prompts/create" className={cn(buttonVariants())}>
          New prompt
        </Link>
      </div>
      <DataTable
        table={table}
        toolbar={<TableSearch value={search} onChange={setSearch} placeholder="Filter by name or slug…" />}
      />
    </div>
  );
}
